import React, { useEffect, useRef, useState } from "react";
import dayjs from "utils/dayjs-config";
import type { CustomCellEditorProps } from "ag-grid-react";
import CIQDatePicker from "components/custom-date-picker";
import {
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_YYYYMMDD,
  DateUtils
} from "utils/dateutils";
import { Button } from "antd";

const POPUP_CLASS = "ciq-date-cell-editor-popup";

// antd v4's rc-picker@2 rendered the popup as
// `<div class="…-panel-container" onMouseDown={e => e.preventDefault()}>`, so
// clicking a day never moved DOM focus off the picker input. antd v6's
// @rc-component/picker@1 dropped that: `onPanelMouseDown` is wired up for
// RangePicker only, and the deleted line still sits commented out in
// PickerInput/Popup/index.js ("Still wish not to lose focus on mouse down").
// Focus now lands on `.ant-picker-panel`, which lives in a portal on
// document.body.
//
// Every log grid that mounts this editor sets `stopEditingWhenCellsLoseFocus`,
// and AG Grid's focusout listener stops editing whenever the newly focused
// element is outside the grid viewport and is not one of AG Grid's own popups.
// The antd portal is neither, so the editor was torn down on mousedown — before
// the click could produce an onChange — and the picked date was silently
// dropped. Restoring the preventDefault keeps focus on the input for the whole
// click. Capture phase so it runs ahead of rc-picker's own handlers.
const keepFocusOnPanelClick = (event: MouseEvent) => {
  if ((event.target as HTMLElement)?.closest?.(`.${POPUP_CLASS}`)) {
    event.preventDefault();
  }
};

export default function CustomDateCellEditor({
  value,
  onValueChange,
  eventKey,
  placeholder,
  stopEditing
}: CustomCellEditorProps & { placeholder?: string }) {
  const pickerRef = useRef<any>(null);

  const [open, setOpen] = useState(false);

  /** Convert incoming AG Grid string → dayjs. `dateTimeObj` always returns a
   * dayjs, and an *invalid* one is a truthy object that would render "Invalid
   * Date" in the input, so screen it out rather than lean on `value` alone. */
  const parsedValue = value ? DateUtils.dateTimeObj(value) : null;
  const initialMoment = parsedValue?.isValid() ? parsedValue : null;

  useEffect(() => {
    document.addEventListener("mousedown", keepFocusOnPanelClick, true);
    return () =>
      document.removeEventListener("mousedown", keepFocusOnPanelClick, true);
  }, []);

  /** On mount: decide initial value + focus + open popup */
  useEffect(() => {
    // Handle keyboard edit start (Backspace, typing, etc.)
    //
    // Nothing is published for the normal path: AG Grid already seeds the editor
    // value from the cell. The previous `onValueChange(initialMoment)` pushed a
    // *dayjs object* while every other path here emits a string, and AG Grid
    // compares pending vs source with `!==` — an object never matched the cell's
    // string, so merely opening this editor and clicking away fired a
    // cellEditRequest, i.e. a save mutation for a date the user never touched.
    if (eventKey === "Backspace") {
      onValueChange(null);
    }

    // Focus + open panel
    setTimeout(() => {
      pickerRef.current?.focus?.();
      setOpen(true);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // IMPORTANT: run only once

  /** When user selects a date */
  const handleChange = (newVal: any) => {
    const parsed = newVal ? dayjs(newVal) : null;

    // `toISOString()` reinterpreted the picked *calendar day* — machine-local
    // midnight — as an instant, so on any UTC+ machine it rolled back a day
    // (IST: Jan-11 00:00 → 2024-01-10T18:30:00.000Z) and the save path, which
    // parses with ['MM-DD-YYYY','YYYY-MM-DD'], stored Jan 10. Read the day off
    // the picker's own local value instead and stamp it with the app's mid-day
    // convention, which is both zone-stable and the shape the detail panel
    // already writes for these fields (components/custom-fields/index.tsx).
    const saveValue = parsed?.isValid()
      ? DateUtils.formatDateWithLunchTime(parsed.format(DATE_FORMAT_YYYYMMDD))
      : null;

    onValueChange(saveValue);

    // Close editor immediately after selection
    stopEditing();
  };

  return (
    <div className="w-full !h-full flex items-center">
      <CIQDatePicker
        ref={pickerRef}
        className="w-full h-8 border-0"
        popupClassName={POPUP_CLASS}
        format={DATE_FORMAT_MMDDYYYY}
        placeholder={placeholder}
        value={initialMoment}
        onChange={handleChange}
        open={open}
        onOpenChange={(o) => setOpen(o)}
        allowClear={false}
        inputReadOnly
        renderExtraFooter={() =>
          initialMoment ? (
            <div className="flex justify-center py-2 w-full">
              <Button
                onClick={() => {
                  handleChange(null);
                }}
                data-testid="index-button-clear-date"
              >
                Clear Date
              </Button>
            </div>
          ) : null
        }
      />
    </div>
  );
}
